'use client';

import { createClient } from '@/lib/supabase/client';

export type BulkOrderItem = {
  productId: string;
  productName: string;
  category: string;
  quantity: number;
  unitPriceMin: number;
  unitPriceMax: number;
};

export type BulkOrder = {
  id: string;
  orderReference: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  items: BulkOrderItem[];
  totalUnits: number;
  estimatedValueMin: number;
  estimatedValueMax: number;
  notes: string;
  orderStatus: 'draft' | 'submitted' | 'processing' | 'confirmed' | 'shipped' | 'completed' | 'cancelled';
  submittedAt: string;
};

export type CreateBulkOrderData = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  items: BulkOrderItem[];
  notes: string;
};

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function generateOrderReference(): string {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PCG-${dateStr}-${rand}`;
}

function mapRow(row: any): BulkOrder {
  return {
    id: row.id,
    orderReference: row.order_reference,
    companyName: row.company_name,
    contactName: row.contact_name,
    email: row.email,
    phone: row.phone,
    items: row.items || [],
    totalUnits: row.total_units,
    estimatedValueMin: row.estimated_value_min,
    estimatedValueMax: row.estimated_value_max,
    notes: row.notes,
    orderStatus: row.order_status,
    submittedAt: row.submitted_at,
  };
}

export const bulkOrderService = {
  async create(data: CreateBulkOrderData): Promise<{ success: boolean; order?: BulkOrder; error?: string }> {
    const supabase = createClient();
    const totalUnits = data.items.reduce((sum, item) => sum + item.quantity, 0);
    const estimatedValueMin = data.items.reduce((sum, item) => sum + item.quantity * item.unitPriceMin, 0);
    const estimatedValueMax = data.items.reduce((sum, item) => sum + item.quantity * item.unitPriceMax, 0);

    try {
      const { data: row, error } = await supabase
        .from('bulk_orders')
        .insert({
          order_reference: generateOrderReference(),
          company_name: data.companyName,
          contact_name: data.contactName,
          email: data.email,
          phone: data.phone,
          items: data.items,
          total_units: totalUnits,
          estimated_value_min: estimatedValueMin,
          estimated_value_max: estimatedValueMax,
          notes: data.notes,
          order_status: 'submitted',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error creating bulk order:', error.message);
          throw error;
        }
        return { success: false, error: error.message };
      }
      return { success: true, order: mapRow(row) };
    } catch (error: any) {
      console.log('Bulk order create error:', error.message);
      return { success: false, error: error.message };
    }
  },

  async getByEmail(email: string): Promise<BulkOrder[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('bulk_orders')
        .select('*')
        .eq('email', email)
        .order('submitted_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching bulk orders:', error.message);
          throw error;
        }
        return [];
      }
      return data?.map(mapRow) || [];
    } catch (error: any) {
      console.log('Bulk order fetch error:', error.message);
      return [];
    }
  },

  async getByReference(reference: string): Promise<BulkOrder | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('bulk_orders')
        .select('*')
        .eq('order_reference', reference)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching bulk order:', error.message);
          throw error;
        }
        return null;
      }
      return data ? mapRow(data) : null;
    } catch (error: any) {
      console.log('Bulk order fetch error:', error.message);
      return null;
    }
  },

  async updateStatus(
    id: string,
    status: BulkOrder['orderStatus']
  ): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('bulk_orders')
        .update({ order_status: status })
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error updating bulk order:', error.message);
          throw error;
        }
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error: any) {
      console.log('Bulk order update error:', error.message);
      return { success: false, error: error.message };
    }
  },

  async delete(id: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('bulk_orders').delete().eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error deleting bulk order:', error.message);
          throw error;
        }
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error: any) {
      console.log('Bulk order delete error:', error.message);
      return { success: false, error: error.message };
    }
  },
};
